<template>
  <div class="grid-y">
    <BreadcrumbWrapper :current-page="currentPage" class="margin-bottom-xs" />
    <div class="grid-x">
      <div class="cell small-12 large-10 large-padding-right-xxl small-margin-bottom-xl large-margin-bottom-z">
        <h1 id="title" class="margin-bottom-m" :class="{ 'small-margin-bottom-s medium-margin-bottom-m': hasError }">
          {{ ORDER_HISTORY.orderHistoryHeading }}
        </h1>
        <Notification v-if="hasError" id="notification" severity="error" :closable="false" class="margin-bottom-m">
          {{ onlineError || inStoreError }}
        </Notification>
        <div>
          <Tabs
            id="desktop-tabs-and-filters"
            :tabs="tabIds"
            :initial-tab="$route.name"
            class="cell"
            :class="tabsClasses"
            @tab-click="selectActiveTab"
          >
            <template v-for="tab in tabs" :key="tab.id" #[`v-tab-head-${tab.id}`]>
              <span :id="tab.id" v-text="tab.label"></span>
            </template>
            <template v-for="tab in tabs" :key="tab.id" #[`v-tab-panel-${tab.id}`]>
              <div class="grid-x align-justify align-middle margin-top-m">
                <FilterDropdown
                  class="cell small-12 medium-4 small-margin-bottom-xs medium-margin-bottom-z"
                  :filter-props="dateFilters($route.name, $route.query?.daterange)"
                  @apply-filter="applyDateFilter"
                />
                <SearchBar v-if="currentTab?.searchable" class="cell small-12 medium-6 large-5" :search-props="searchProps" />
              </div>
              <OnlineHistory v-if="tab.id === 'online-history'" />
              <InStoreHistory v-if="tab.id === 'in-store-history'" />
            </template>
          </Tabs>
        </div>
        <NeedHelp
          class="text-center align-center padding-top-m border-top padding-bottom-m border-bottom"
          :show-chat="currentTab?.id === 'online-history'"
        />
        <MedalliaSurvey class="padding-top-m" :class="{ 'padding-bottom-m border-bottom': envProps.isMew }" />
      </div>
      <div class="cell small-12 large-2">
        <ProsWrapper v-if="killSwitch && Object.keys(killSwitch).length" data-context="PR_ZONE_A" :killswitches="killSwitch" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeMount, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useTitle } from '@vueuse/core';
import Notification from '@atomic-ui/notification/Notification';
import Tabs from '@atomic-ui/tabs/Tabs';
import { fireTag } from '@vue-util/tagManager';
import BreadcrumbWrapper from '@/components/common/BreadcrumbWrapper';
import NeedHelp from '@/components/common/NeedHelp';
import MedalliaSurvey from '@/components/MedalliaSurvey';
import SearchBar from '@/components/common/SearchBar';
import FilterDropdown from '@/components/common/FilterDropdown';
import OnlineHistory from '@/components/pages/ppe/OnlineHistory';
import InStoreHistory from '@/components/pages/ppe/InStoreHistory';
import ProsWrapper from '@/components/ProsWrapper';
import { ORDER_HISTORY, brandName } from 'config';
import { useInstoreOrderHistoryStore } from '@/stores/ppe/useInstoreOrderHistoryStore';
import { useOrderHistoryStore } from '@/stores/ppe/useOrderHistoryStore';
import { useOnlineOrderHistoryStore } from '@/stores/ppe/useOnlineOrderHistoryStore';
import { useEnvPropsStore } from '@/stores/useEnvPropsStore';

const route = useRoute();
const router = useRouter();

const orderHistoryStore = useOrderHistoryStore();
const instoreOrderHistoryStore = useInstoreOrderHistoryStore();
const onlineOrderHistoryStore = useOnlineOrderHistoryStore();
const envPropsStore = useEnvPropsStore();

const currentPage = ref('P&R');

const tabs = computed(() => orderHistoryStore.tabs);
const envProps = computed(() => envPropsStore.envProps);
const selectedDate = computed(() => orderHistoryStore.selectedDate);
const onlineError = computed(() => onlineOrderHistoryStore.error);
const inStoreError = computed(() => instoreOrderHistoryStore.error?.message);
const hasError = computed(() => onlineError.value || inStoreError.value);
const killSwitch = computed(() => orderHistoryStore.killSwitch);
const currentTab = computed(() => {
  const tab = tabs.value.find((t) => t.id === route.name);
  if (tab === -1) return {};
  return tab;
});
const searchProps = computed(() => ({
  placeholder: ORDER_HISTORY.searchPlaceholder,
}));

const tabsClasses = computed(() => ({
  'margin-bottom-s': route.name === 'in-store-history',
  'margin-bottom-m': route.name === 'online-history' && onlineError.value,
}));

const tabIds = computed(() => orderHistoryStore.GET_TAB_IDS);
const dateFilters = computed(() => orderHistoryStore.GET_DATE_FILTERS);

onBeforeMount(async () => {
  try {
    await orderHistoryStore.SET_SELECTED_DATE_RANGE({ tabName: route.name, dateRange: route.query?.daterange });
  } catch (error) {
    const firstError = error?.response?.data?.errors?.error?.[0];
    fireTag('link', {
      ...ORDER_HISTORY.tag?.error,
      error_code: [firstError?.errorCode || ORDER_HISTORY.tag?.defaultErrorCode],
      error_message: [firstError?.message],
    });
  }
});

const medalliaSurveyInit = () => {
  const MEDALLIA_SURVEY = window.KAMPYLE_ONSITE_SDK;
  if (!MEDALLIA_SURVEY || typeof MEDALLIA_SURVEY.updatePageView !== 'function') return;
  MEDALLIA_SURVEY.updatePageView();
};

onMounted(() => {
  useTitle(`${ORDER_HISTORY.orderHistoryHeading} - ${brandName}`);
  window.medalliaSurveyData = 'in Order History';
  medalliaSurveyInit();
});

const selectActiveTab = (index, activeTab, tabName) => {
  const queryParams = { ...route.query };
  if (queryParams.daterange) delete queryParams.daterange;
  const selectedDaterange = selectedDate.value[tabName]?.dateRange;
  router.replace({
    name: tabName,
    query: {
      ...queryParams,
      ...(selectedDaterange && { daterange: selectedDaterange }),
    },
  });
};

const applyDateFilter = async (selectedDateValue) => {
  if (route.name === 'online-history') {
    fireTag('link', {
      ...ORDER_HISTORY.applyDateFilterTag,
    });
  }
  await orderHistoryStore.SET_SELECTED_DATE_RANGE({ tabName: route.name, value: selectedDateValue });
  router.replace({
    name: route.name,
    query: {
      ...route.query,
      daterange: selectedDate.value[route.name]?.dateRange,
    },
  });
};
</script>
